import React, { useState } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import api from '../services/api.js';
import Message from '../components/Message.jsx';

function ShiftMaster() {
  const { state, loadShifts } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [formData, setFormData] = useState({
    shiftName: '',
    days: [],
    shiftStart: '',
    shiftEnd: '',
    workingHours: '',
    otStart: '',
    otEnd: ''
  });
  const [message, setMessage] = useState({ text: '', type: '' });

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
if (!daysOfWeek || daysOfWeek.length === 0) {
  throw new Error('Days of week is required');
}
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.days.length === 0) {
      setMessage({ text: 'Please select at least one day!', type: 'error' });
      return;
    }
    
    // Validate OT fields - if OT Start is filled, OT End is required
    if (formData.otStart && !formData.otEnd) {
      setMessage({ 
        text: 'OT End time is required when OT Start is filled', 
        type: 'error' 
      });
      return;
    }
    
    // Validate OT Start must be after Shift End
    if (formData.otStart && formData.shiftEnd && formData.otStart <= formData.shiftEnd) {
      setMessage({ 
        text: 'OT Start must be after Shift End time', 
        type: 'error' 
      });
      return;
    }
    
    // Validate shift duration against working hours
    if (formData.shiftStart && formData.shiftEnd && formData.workingHours) {
      const startTime = new Date(`1970-01-01T${formData.shiftStart}:00`);
      const endTime = new Date(`1970-01-01T${formData.shiftEnd}:00`);
      
      let shiftDuration;
      if (endTime < startTime) {
        // Cross-midnight shift
        shiftDuration = (24 * 60 * 60 * 1000 - (startTime - endTime)) / (1000 * 60 * 60);
      } else {
        shiftDuration = (endTime - startTime) / (1000 * 60 * 60);
      }
      
      if (shiftDuration > formData.workingHours) {
        setMessage({ 
          text: `Shift duration (${shiftDuration.toFixed(1)} hours) cannot exceed working hours (${formData.workingHours} hours)`, 
          type: 'error' 
        });
        return;
      }
    }
    
    try {
      const shiftData = {
        ...formData,
        workingHours: parseFloat(formData.workingHours)
      };
      
      if (editingShift) {
        await api.shifts.update(editingShift.id, shiftData);
        setMessage({ text: 'Shift updated successfully!', type: 'success' });
      } else {
        await api.shifts.create(shiftData);
        setMessage({ text: 'Shift created successfully!', type: 'success' });
      }
      loadShifts();
      resetForm();
    } catch (error) {
      console.error('Error saving shift:', error);
      const errorMsg = error.response?.data?.error || 'Error saving shift!';
      setMessage({ text: errorMsg, type: 'error' });
    }
  };

  const resetForm = () => {
    setFormData({
      shiftName: '',
      days: [],
      shiftStart: '',
      shiftEnd: '',
      workingHours: '',
      otStart: '',
      otEnd: ''
    });
    setShowModal(false);
    setEditingShift(null);
  };

  const handleEdit = (shift) => {
    setEditingShift(shift);
    setFormData(shift);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await api.shifts.delete(id);
        loadShifts();
        setMessage({ text: 'Shift deleted successfully!', type: 'success' });
      } catch (error) {
        console.error('Error deleting shift:', error);
        setMessage({ text: 'Error deleting shift!', type: 'error' });
      }
    }
  };

  const handleDayChange = (day) => {
    const updatedDays = formData.days.includes(day)
      ? formData.days.filter(d => d !== day)
      : [...formData.days, day];
    setFormData({...formData, days: updatedDays});
  };

  return (
    <div className="shift-master">
      {message.text && (
        <Message 
          message={message.text} 
          type={message.type} 
          onClose={() => setMessage({ text: '', type: '' })} 
        />
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Shift Master</h2>
        <button className="btn btn-outline-primary" onClick={() => setShowModal(true)}>
          Add Shift
        </button>
      </div>

      {showModal && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.3)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-sm border-0">
              <div className="modal-header border-bottom" style={{backgroundColor: 'var(--primary-light)'}}>
                <h6 className="modal-title" style={{color: 'var(--primary-color)'}}>{editingShift ? 'Edit' : 'Add'} Shift</h6>
                <button type="button" className="btn-close" onClick={resetForm}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-3">
                  <div className="row g-2">
                    <div className="col-12">
                      <label className="form-label small text-muted">Shift Name *</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={formData.shiftName}
                        onChange={(e) => setFormData({...formData, shiftName: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small text-muted">Days *</label>
                      <div className="d-flex flex-wrap gap-2">
                        {daysOfWeek.map(day => (
                          <div key={day} className="form-check form-check-sm">
                            <input className="form-check-input" type="checkbox" checked={formData.days.includes(day)} onChange={() => handleDayChange(day)} />
                            <label className="form-check-label small text-muted">{day.slice(0,3)}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted">Shift Start *</label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        value={formData.shiftStart}
                        onChange={(e) => setFormData({...formData, shiftStart: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted">Shift End *</label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        value={formData.shiftEnd}
                        onChange={(e) => setFormData({...formData, shiftEnd: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label small text-muted">Working Hours *</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={formData.workingHours}
                        onChange={(e) => setFormData({...formData, workingHours: e.target.value})}
                        min="1"
                        max="24"
                        step="0.5"
                        placeholder="Enter working hours"
                        required
                      />
                      <small className="text-muted">Maximum hours per shift (shift duration cannot exceed this)</small>
                    </div>

                    <div className="col-6">
                      <label className="form-label small text-muted">OT Start </label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        value={formData.otStart}
                        onChange={(e) => setFormData({...formData, otStart: e.target.value})}
                        
                      />
                      {formData.otStart < formData.shiftEnd && (
                        <small className="text-danger">OT Start must be after Shift End</small>
                      )}
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted">OT End {formData.otStart ? '*' : ''}</label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        value={formData.otEnd}
                        onChange={(e) => setFormData({...formData, otEnd: e.target.value})}
                        required={formData.otStart ? true : false}
                      />
                      {formData.otStart && !formData.otEnd && (
                        <small className="text-danger">Required when OT Start is filled</small>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light py-2">
                  <button type="submit" className="btn btn-sm btn-primary">
                    Save
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={resetForm}>
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Shift Name</th>
                  <th>Days</th>
                  <th>Shift Time</th>
                  <th>Working Hours</th>
                  <th>OT Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(state.shifts || []).map(shift => (
                  <tr key={shift.id}>
                    <td>{shift.shiftName}</td>
                    <td>{shift.days.join(', ')}</td>
                    <td>{shift.shiftStart} - {shift.shiftEnd}</td>
                    <td>{shift.workingHours || 8} hrs</td>
                    <td>{shift.otStart} - {shift.otEnd}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleEdit(shift)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-outline-warning" onClick={() => handleDelete(shift.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShiftMaster;